import React, { useEffect } from 'react';
import './App.css';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';

import NavBar from './components/NavBar';
import Footer from './components/Footer';

import Hero from './components/Hero';
import About from './components/About';
import DemoForfait from './components/DemoForfait';
import Services from './components/Services';
import Stats from './components/Stats';
import Asavoir from './components/Asavoir';
import Contact from './components/contact';

const FAQ = React.lazy(() => import('./components/FAQ'));
const Simulator = React.lazy(() => import('./components/Simulator'));
const Optimizer = React.lazy(() => import('./components/Optimizer'));
const Activity = React.lazy(() => import('./components/Activity'));
const Historique = React.lazy(() => import('./components/Historique'));
const Login = React.lazy(() => import('./components/Login'));
const ForgotPassword = React.lazy(() => import('./components/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./components/ResetPassword'));
const VerifyEmail = React.lazy(() => import('./components/VerifyEmail'));
const Upload = React.lazy(() => import('./components/Upload'));
const Analyse = React.lazy(() => import('./components/Analyse'));
const Payment = React.lazy(() => import('./components/Payment'));
const PaymentSuccess = React.lazy(() => import('./components/PaymentSuccess'));
const PaymentFail = React.lazy(() => import('./components/PaymentFail'));
const Results = React.lazy(() => import('./components/Results'));
const ResultsReport = React.lazy(() => import('./components/ResultsReport'));
const Documentation = React.lazy(() => import('./components/Documentation'));
import { UserProvider, useUser } from './components/UserContext';
import { ContentProvider } from './components/ContentContext';
import { LanguageProvider } from './components/LanguageContext';
import { UIProvider } from './components/UIContext';



/**
 * Composant utilitaire: à chaque changement de route,
 * - remonte la page en haut
 * - si un identifiant de section est fourni via state {scrollTo: 'about'} ou via hash (#about),
 *   on scrolle en douceur vers cette section de la page d'accueil.
 */
function ScrollOnRouteChange() {
  const location = useLocation();

  useEffect(() => {
    // remonter en haut sur chaque navigation
    window.scrollTo({ top: 0, behavior: 'instant' });

    const targetFromState = location.state && location.state.scrollTo;
    const targetFromHash = location.hash ? location.hash.replace('#', '') : null;
    const target = targetFromState || targetFromHash;

    if (target) {
      // attendre que le DOM soit peint
      setTimeout(() => {
        const el = document.getElementById(target);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  }, [location]);

  return null;
}

/** Page d'accueil qui regroupe vos sections existantes. */
function Home() {
  return (
    <>
      <div id="home"><Hero /></div>
      <div id="services"><Services /></div>
      {/* <div id="about"><About /></div> */}
      <div id="demo"><DemoForfait /></div>
      <div id="asavoir"><Asavoir /></div>
      <div id="stats"><Stats /></div>
      {/* <div id="contact"><Upload /></div> */}
      <div id="contact"><Contact /></div>
      {/* <div id="admin"><Admin /></div> */}
    </>
  );
}

/** Layout commun: NavBar en haut, Footer en bas, contenu au milieu. */
function Layout({ children }) {
  return (

    <div className="app-shell">
      <NavBar />
      <ScrollOnRouteChange />
      <main>{children}</main>
      <Footer />
    </div>

  );
}

// Garde d'authentification pour protéger certaines routes (ex: Analyse)
function RequireAuth({ children }) {
  const { user } = useUser();
  const location = useLocation();
  if (!user) {
    // Redirige vers /login et mémorise la route cible
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      flexDirection: 'column',
      gap: '16px'
    }}>
      <div className="loading-spinner" style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(47, 68, 136, 0.1)',
        borderTop: '3px solid var(--color-primary, #2f4488)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <UserProvider>
        <ContentProvider>
          <UIProvider>
            <React.Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Layout><Home /></Layout>} />
                <Route path="/login" element={<Layout><Login /></Layout>} />
                <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
                <Route path="/reset-password" element={<Layout><ResetPassword /></Layout>} />
                <Route path="/verify-email" element={<Layout><VerifyEmail /></Layout>} />
                <Route path="/upload" element={<Layout><Upload /></Layout>} />
                <Route
                  path="/analyse"
                  element={
                    <RequireAuth>
                      <Layout><Analyse /></Layout>
                    </RequireAuth>
                  }
                />
                <Route path="/payment" element={<Layout><Payment /></Layout>} />
                <Route path="/payment-success" element={<Layout><PaymentSuccess /></Layout>} />
                <Route path="/payment-failed" element={<Layout><PaymentFail /></Layout>} />
                {/* redirection douce en cas d'URL inconnue */}
                <Route path="*" element={<Layout><Home /></Layout>} />
                <Route path="/results" element={<Results />} />
                <Route path="/results/report" element={<ResultsReport />} />
                <Route path="/faq" element={<Layout><FAQ /></Layout>} />
                <Route path="/documentation" element={<Layout><Documentation /></Layout>} />
                <Route path="/simulateur" element={<Simulator />} />
                <Route path="/optimisation" element={<Optimizer />} />
                <Route path="/activite" element={<Activity />} />
                <Route path="/historique" element={<RequireAuth><Layout><Historique /></Layout></RequireAuth>} />
              </Routes>
            </React.Suspense>
          </UIProvider>
        </ContentProvider>
      </UserProvider>
    </LanguageProvider>
  );
}
